import { useEffect, useRef } from 'react';
import mermaid from 'mermaid';
import { BRAND_COLORS } from '@/theme';

interface MermaidDiagramProps {
  diagram: string;
  id: string;
  className?: string;
}

/**
 * The palette for a diagram comes from `src/theme.ts`, never from the diagram's own text.
 *
 * WHY THIS IS A COMPONENT CONCERN AND NOT A CONTENT ONE. A flowchart authored with hardcoded
 * `classDef fill:#FFFFFF` renders light nodes inside this app's dark surface — measured
 * 2026-10-10 on blog29, where every node was white on slate-800 and the connectors were
 * near-invisible. Worse, one hardcoded classDef used `#0AB3BF`, our brand teal, to highlight a
 * node belonging to the COMPARED system, so our own colour told the reader that the other
 * company's agent was the important one.
 *
 * So a diagram declares only what a node IS — `ours`, `theirs`, `gate`, `act`, `human`, `soft` —
 * and this component decides what that LOOKS like, per theme. Two consequences worth the
 * indirection: the same diagram stays legible in light and dark without a second copy, and our
 * brand colour can only ever mark our own side.
 *
 * `meet`, not `slice`: `slice` scales the drawing to FILL its box and crops the overflow, which
 * cut the bottom row off the comparison figure. A diagram that silently loses its last row is a
 * diagram that is wrong, not merely tight.
 */

/** Semantic roles a diagram may use. Anything else falls back to mermaid's own theme. */
const ROLE_PALETTE = {
  dark: {
    ours: { fill: BRAND_COLORS.tealDark, stroke: BRAND_COLORS.teal, text: BRAND_COLORS.white },
    theirs: { fill: '#334155', stroke: '#64748B', text: '#F1F5F9' },
    gate: { fill: '#B45309', stroke: BRAND_COLORS.warning, text: BRAND_COLORS.white },
    act: { fill: '#B91C1C', stroke: BRAND_COLORS.error, text: BRAND_COLORS.white },
    human: { fill: '#1D4ED8', stroke: BRAND_COLORS.info, text: BRAND_COLORS.white },
    soft: { fill: '#1E293B', stroke: '#475569', text: '#CBD5E1' },
  },
  light: {
    ours: { fill: BRAND_COLORS.teal, stroke: BRAND_COLORS.tealDark, text: BRAND_COLORS.white },
    theirs: { fill: '#E2E8F0', stroke: '#64748B', text: '#1E293B' },
    gate: { fill: BRAND_COLORS.warningLight, stroke: '#B45309', text: '#78350F' },
    act: { fill: BRAND_COLORS.errorLight, stroke: '#B91C1C', text: '#7F1D1D' },
    human: { fill: BRAND_COLORS.infoLight, stroke: '#1D4ED8', text: '#1E3A8A' },
    soft: { fill: '#F1F5F9', stroke: '#CBD5E1', text: '#334155' },
  },
} as const;

type RoleName = keyof typeof ROLE_PALETTE.dark;

/**
 * Appends the palette as `classDef` statements, so the diagram text carries roles and nothing else.
 * Appended rather than prepended because the first line is the `flowchart TB` header, and mermaid
 * reads the last definition of a class name, so this wins over anything left in the content.
 */
const withBrandClassDefs = (diagram: string, isDark: boolean): string => {
  const palette = isDark ? ROLE_PALETTE.dark : ROLE_PALETTE.light;
  const defs = (Object.keys(palette) as RoleName[])
    .map((role) => {
      const { fill, stroke, text } = palette[role];
      return `    classDef ${role} fill:${fill},stroke:${stroke},stroke-width:2px,color:${text}`;
    })
    .join('\n');
  return `${diagram}\n${defs}`;
};

export const MermaidDiagram = ({ diagram, id, className = '' }: MermaidDiagramProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  /** Keeps every `mermaid.render()` call on its own temp id, so two diagrams cannot collide. */
  const renderCount = useRef(0);

  useEffect(() => {
    let cancelled = false;

    const renderDiagram = async () => {
      const container = containerRef.current;
      if (!container) return;

      const isDark = document.documentElement.classList.contains('dark');

      mermaid.initialize({
        startOnLoad: false,
        securityLevel: 'loose',
        theme: isDark ? 'dark' : 'default',
        themeVariables: isDark
          ? {
              // Every value traces to src/theme.ts. The previous palette hardcoded slate hexes
              // here while the nodes hardcoded white, which is how one figure ended up with two
              // colour languages and no relationship to the app.
              primaryColor: '#1E293B',
              primaryTextColor: '#F1F5F9',
              primaryBorderColor: BRAND_COLORS.teal,
              lineColor: '#64748B',
              secondBkgColor: '#334155',
              tertiaryColor: '#475569',
              fontSize: '18px',
              fontFamily: 'system-ui, -apple-system, sans-serif',
            }
          : {
              primaryColor: '#F1F5F9',
              primaryTextColor: '#1E293B',
              primaryBorderColor: BRAND_COLORS.teal,
              lineColor: '#94A3B8',
              secondBkgColor: '#E2E8F0',
              tertiaryColor: '#F8FAFC',
              fontSize: '18px',
              fontFamily: 'system-ui, -apple-system, sans-serif',
            },
      });

      try {
        // Scope the render to THIS component. `mermaid.run()` scans the whole document for
        // `.mermaid` nodes, so two diagrams on one page (or a theme re-render) race each other and
        // throw on the nodes the other one already converted. `mermaid.render()` returns the SVG
        // for this diagram only.
        const { svg } = await mermaid.render(
          `${id}-svg-${renderCount.current++}`,
          withBrandClassDefs(diagram, isDark),
        );
        if (cancelled || !containerRef.current) return;

        containerRef.current.innerHTML = svg;

        const svgEl = containerRef.current.querySelector('svg');
        if (svgEl) {
          // Mermaid caps width and pins a viewBox; this app wants a responsive drawing.
          svgEl.removeAttribute('style');
          svgEl.setAttribute('width', '100%');
          // No height attribute at all: `height="auto"` is not a valid SVG length (the browser
          // logs "Expected length, auto" and ignores it), and leaving it off lets the viewBox set
          // the aspect. `meet` CONTAINS the drawing where `slice` cropped the bottom row off the
          // tallest figure.
          svgEl.removeAttribute('height');
          svgEl.setAttribute('preserveAspectRatio', 'xMidYMid meet');
          svgEl.style.display = 'block';
          svgEl.style.maxWidth = '100%';
        }
      } catch (error) {
        console.error('Mermaid diagram rendering error:', error);
      }
    };

    renderDiagram();

    // Listen for theme changes
    const observer = new MutationObserver(() => {
      renderDiagram();
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });

    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [diagram, id]);

  return (
    <div
      ref={containerRef}
      className={`flex justify-center items-center my-8 p-6 rounded-lg border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900 overflow-x-auto ${className}`}
      id={id}
    />
  );
};
